import { memo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import FeedbackMessage from "../../Login/LoginComponents/MainBox/FeedbackMessage";
import SignUpCardFields from "./SignUpCardFields";
import { validateForm } from "../SignUpFormFunctions/validateForm";
import { handleRegister } from "../SignUpFormFunctions/handleRegister";
import KsbActionButton from "../../../../components/KsbActionButton";
import { PersonAdd } from "@mui/icons-material";
import { useAppContext } from "../../../../context/appContext";

function SignUpCard({ flipCard, seeLogin }) {
  const theme = useTheme();
  const { isMobile } = useAppContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // ✅ FORM STATE
  const [formState, setFormState] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    passwordConfirmation: "",
    abbreviation: "",
    schools: [],
  });

  // ✅ ERRORS
  const [errors, setErrors] = useState({
    emailErr: {},
    firstNameErr: {},
    lastNameErr: {},
    passwordErr: {},
    passwordConfirmationErr: {},
    abbreviationErr: {},
    schoolsErr: "",
  });

  const [error, setError] = useState([]);
  const [msg, setMsg] = useState("");

  // ✅ teacher detection
  const isTeacher = (() => {
    const localPart = (formState.email || "").split("@")[0];
    return localPart.includes(".") && !localPart.includes("_");
  })();

  // ✅ CHANGE HANDLER
  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setError([]);
    setMsg("");

    setFormState((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "email" ? { abbreviation: "" } : {}),
    }));

    setErrors((prev) => ({
      ...prev,
      [`${field}Err`]: {},
      ...(field === "password" || field === "passwordConfirmation"
        ? { passwordErr: {}, passwordConfirmationErr: {} }
        : {}),
      ...(field === "abbreviation" ? { abbreviationErr: {} } : {}),
    }));
  };

  // ✅ SCHOOL HANDLER
  const handleSchoolChange = (school) => {
    setFormState((prev) => {
      const exists = prev.schools.includes(school);

      return {
        ...prev,
        schools: exists
          ? prev.schools.filter((s) => s !== school)
          : [...prev.schools, school],
      };
    });

    setErrors((prev) => ({
      ...prev,
      schoolsErr: "",
    }));
  };

  // ✅ SUBMIT
  const submit = async () => {
    setError([]);
    setMsg("");
    setLoading(true); // ✅ start spinner

    const { isValid, validationErrors } = validateForm(formState, isTeacher);

    if (!isValid) {
      setErrors(validationErrors);
      setLoading(false); // ✅ stop spinner

      return;
    }

    try {
      await handleRegister(formState);

      // ✅ little UX delay
      setTimeout(() => {
        navigate("/verify-info");
      }, 1200);
    } catch (err) {
      console.error("❌ REGISTER ERROR:", err);

      if (err.response) {
        if (err.response.status === 409) {
          setError(["Diese E-Mail ist bereits registriert."]);
        } else {
          setError([err.response.data?.message || "Serverfehler"]);
        }
      } else {
        setError([
          "Keine Verbindung zum Server. Bitte später erneut versuchen.",
        ]);
      }

      setLoading(false); // ✅ stop spinner on error
    }
  };

  // ✅ SCHOOL OPTIONS
  const SCHOOL_OPTIONS = ["KSBer", "andere"];

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2,
        gap: isMobile ? 1 : 2,
      }}
    >
      {/* ✅ TITLE */}
      <Typography variant="h3" mt={2}>
        Registrierung
      </Typography>

      {/* ✅ FORM */}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "90%",
          gap: isMobile ? 0 : 1,
        }}
      >
        <SignUpCardFields
          fields={{
            firstName: {
              type: "text",
              placeholder: "Vorname",
              value: formState.firstName,
              onChange: handleChange("firstName"),
              error: errors.firstNameErr,
              autoComplete: "given-name",
            },
            lastName: {
              type: "text",
              placeholder: "Nachname",
              value: formState.lastName,
              onChange: handleChange("lastName"),
              error: errors.lastNameErr,
              autoComplete: "family-name",
            },
            email: {
              type: "email",
              placeholder: "Email",
              value: formState.email,
              onChange: handleChange("email"),
              error: errors.emailErr,
              autoComplete: "email",
            },
            ...(isTeacher
              ? {
                  abbreviation: {
                    type: "text",
                    placeholder: "Kürzel (2–3 Buchstaben)",
                    value: formState.abbreviation,
                    onChange: handleChange("abbreviation"),
                    error: errors.abbreviationErr,
                    autoComplete: "off",
                  },
                }
              : {}),
            password: {
              type: "password",
              placeholder: "Passwort",
              value: formState.password,
              onChange: handleChange("password"),
              error: errors.passwordErr,
              autoComplete: "new-password",
            },
            passwordConfirmation: {
              type: "password",
              placeholder: "Passwort wiederholen",
              value: formState.passwordConfirmation,
              onChange: handleChange("passwordConfirmation"),
              error: errors.passwordConfirmationErr,
              autoComplete: "new-password",
            },
          }}
        />

        {/* ✅ SCHOOL SELECTOR */}
        <Box sx={{ width: "100%", mt: 1 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: theme.palette.text.inactive,
              mb: 0.5,
              textAlign: "left",
            }}
          >
            Schule *
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            {SCHOOL_OPTIONS.map((school) => {
              const selected = formState.schools.includes(school);

              return (
                <Box
                  key={school}
                  onClick={() => handleSchoolChange(school)}
                  sx={{
                    flex: 1,
                    padding: "10px",
                    borderRadius: "10px",
                    textAlign: "center",
                    cursor: "pointer",
                    fontSize: "14px",
                    backgroundColor: selected
                      ? theme.palette.primary.light
                      : "#d7e5f8",
                    boxShadow: selected
                      ? `inset 0 0 0 2px ${theme.palette.background.special}`
                      : "none",
                    color: theme.palette.primary.main,
                  }}
                >
                  {school}
                </Box>
              );
            })}
          </Box>

          {errors.schoolsErr && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: theme.palette.text.red,
                mt: 0.5,
              }}
            >
              {errors.schoolsErr}
            </Typography>
          )}
        </Box>

        {/* ✅ BUTTON */}

        <Box sx={{ mt: 3 }}>
          <KsbActionButton
            onClick={submit}
            label="Registrieren"
            loading={loading}
            fullWidth
            icon={<PersonAdd />}
            sx={{
              bgcolor: theme.palette.primary.main,
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
          />
        </Box>

        {/* ✅ MESSAGE */}
        <FeedbackMessage errors={error} msg={msg} />
      </Box>
    </Box>
  );
}

export default memo(SignUpCard);
